import { connect } from 'react-redux'
import withApiError, {
  ERROR_TYPE_REPORTING,
} from 'layers/errorHandling/apiError'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import AdminSingleSiteView from './component'
import { selectors as licenseSelectors } from 'store/licenseManager'
import {
  operations as reportsOperations,
  selectors as reportsSelectors,
} from 'store/reportsManager'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL } from 'components/AdminSiteHeader/constants'
import { isFlagOn } from 'utils/featureFlags'

export const AdminSingleSiteViewContainer = ({
  email,
  fetchLessonCompletionData,
  fetchLessonCompletionDataForDWB,
  isFetchingLessons,
  lessonCompletionToggleView,
  lastUpdatedAt,
  lessonCompletionUnitsLabel,
  programKey,
  showEmptyDwbReport,
  showLessonCompletionExport,
  siteId,
  sites,
  sitesPreferences,
  summary,
}) => {
  useEffect(() => {
    if (!email || !programKey) return

    if (
      lessonCompletionToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS &&
      siteId
    ) {
      fetchLessonCompletionDataForDWB({
        adminEmail: email,
        toggleView: lessonCompletionToggleView,
        program: programKey,
        siteId,
      })
    } else {
      fetchLessonCompletionData({
        adminEmail: email,
        toggleView: lessonCompletionToggleView,
        program: programKey,
      })
    }
  }, [
    email,
    programKey,
    lessonCompletionToggleView,
    lessonCompletionUnitsLabel,
    siteId,
  ])

  const isCoreUnitsView = lessonCompletionToggleView === TOGGLE_VIEW.CORE_UNITS
  const isSpecializedUnitsView =
    lessonCompletionToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS

  return (
    <AdminSingleSiteView
      isCoreUnitsView={isCoreUnitsView}
      isFetchingLessons={isFetchingLessons}
      isSpecializedUnitsView={isSpecializedUnitsView}
      lastUpdatedAt={lastUpdatedAt}
      lessonCompletionUnitsLabel={lessonCompletionUnitsLabel}
      showEmptyDwbReport={showEmptyDwbReport}
      showLessonCompletionExport={showLessonCompletionExport}
      sites={sites}
      sitesPreferences={sitesPreferences}
      summary={summary}
    />
  )
}

AdminSingleSiteViewContainer.propTypes = {
  email: PropTypes.string,
  fetchLessonCompletionData: PropTypes.func,
  fetchLessonCompletionDataForDWB: PropTypes.func,
  isFetchingLessons: PropTypes.bool,
  lastUpdatedAt: PropTypes.string,
  lessonCompletionToggleView: PropTypes.string,
  lessonCompletionUnitsLabel: PropTypes.string.isRequired,
  programKey: PropTypes.string,
  showEmptyDwbReport: PropTypes.bool,
  showLessonCompletionExport: PropTypes.bool,
  siteId: PropTypes.number,
  sites: PropTypes.array,
  sitesPreferences: PropTypes.object,
  summary: PropTypes.object,
}

const mapDispatchToProps = {
  fetchLessonCompletionData: reportsOperations.fetchLessonCompletionData,
  fetchLessonCompletionDataForDWB:
    reportsOperations.fetchLessonCompletionDataForDWB,
}

export const mapStateToProps = state => {
  const {
    reportsManager: { isFetchingAdminLessonCompletionData: isFetchingLessons },
    userProfileManager: { profile },
  } = state

  const { email } = profile
  const programKey = reportsSelectors.selectAdminLessonCompletionProgramKey(
    state,
  )
  const lessonCompletionToggleView = reportsSelectors.selectAdminLessonCompletionToggleView(
    state,
  )
  const sites = reportsSelectors.selectSortedAdminLessonCompletionData(state)
  const sitesPreferences = licenseSelectors.selectActiveAdminSitePreferences(
    state,
  )
  const siteId = licenseSelectors.selectAdminLessonCompletionSingleSiteId(state)

  const isDwbSingleSiteFetch =
    lessonCompletionToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS && siteId

  const summary = isDwbSingleSiteFetch
    ? reportsSelectors.selectAdminLessonCompletionDwbSummaryData(state)
    : reportsSelectors.selectAdminLessonCompletionSummaryData(state)

  const lastUpdatedAt = summary?.etlLastUpdatedAt || null

  const lessonCompletionUnitsLabel =
    lessonCompletionToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS
      ? LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL
      : 'Core Units'

  const showLessonCompletionExport =
    lessonCompletionToggleView !== TOGGLE_VIEW.SPECIALIZED_UNITS

  const isSpecializedView =
    lessonCompletionToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS
  const isDwbReportingEnabled = isFlagOn(['feature_LEARN-25816-dwb-reporting'])

  const showEmptyDwbReport = isDwbReportingEnabled
    ? isSpecializedView && !summary
    : isSpecializedView

  return {
    email,
    isFetchingLessons,
    lessonCompletionToggleView,
    lastUpdatedAt,
    lessonCompletionUnitsLabel,
    programKey,
    showEmptyDwbReport,
    showLessonCompletionExport,
    siteId,
    sites,
    sitesPreferences,
    summary,
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withApiError(AdminSingleSiteViewContainer, [ERROR_TYPE_REPORTING]))
